import { useRef, useState } from 'react';
import type { UploadState } from '../types';

interface FileUploaderProps {
  onUpload: (file: File) => void;
  state: UploadState;
  disabled: boolean;
  onReset: () => void;
}

export function FileUploader({
  onUpload,
  state,
  disabled,
  onReset,
}: FileUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    if (state.stage !== 'idle') {
      onReset();
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (file && !disabled) {
      onUpload(file);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStageLabel = (): string => {
    switch (state.stage) {
      case 'initializing':
        return 'Initializing upload...';
      case 'uploading':
        return `Uploading... ${state.progress}%`;
      case 'completing':
        return 'Linking to Webflow...';
      case 'done':
        return 'Upload complete!';
      case 'error':
        return 'Upload failed';
      default:
        return '';
    }
  };

  return (
    <div className={`card ${disabled ? 'disabled' : ''}`}>
      <h2 className="card-title">
        <span className="step-number">3</span>
        Upload File
      </h2>
      {disabled ? (
        <p className="muted">Select a collection and item first</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div
            className={`drop-zone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              className="file-input"
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            />
            {file ? (
              <div className="file-info">
                <span className="file-icon">📄</span>
                <div className="file-details">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  type="button"
                  className="remove-file"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileChange(null);
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="drop-zone-content">
                <span className="drop-icon">📁</span>
                <p>Drop file here or click to select</p>
              </div>
            )}
          </div>

          {state.stage !== 'idle' && (
            <div className={`upload-status ${state.stage}`}>
              {state.stage === 'uploading' && (
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${state.progress}%` }}
                  />
                </div>
              )}
              <p className="status-label">{getStageLabel()}</p>
              {state.error && <p className="error-message">{state.error}</p>}
              {state.publicUrl && (
                <p className="success-message">
                  Download URL: <a href={state.publicUrl} target="_blank" rel="noopener noreferrer">{state.publicUrl}</a>
                </p>
              )}
            </div>
          )}

          <div className="button-group">
            <button
              type="submit"
              className="button primary"
              disabled={!file || state.isUploading}
            >
              {state.isUploading ? 'Uploading...' : 'Upload & Link'}
            </button>
            {(state.stage === 'done' || state.stage === 'error') && (
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  setFile(null);
                  onReset();
                }}
              >
                Start Over
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
